import axios from 'axios'

import config from 'config'
import type {SupportedLanguage} from '~/i18n'

const baseUrl = config.endpoints.auth

interface CheckEmail {
    email: string
    inviteToken?: string
}

export async function checkEmail(payload: CheckEmail): Promise<void> {
    const result = await axios.request<void>({
        url: `${baseUrl}/sca/register/check`,
        method: 'post',
        withCredentials: true,
        data: payload,
    })
    return result.data
}

interface RegisterAdminWithOtpRequest {
    captchaToken?: string
    firstName: string
    lastName?: string
    jobTitle?: string
    email: string
    passcode: string
    phone: string
    language?: SupportedLanguage
    inviteToken?: string
    otpType: OtpType
}

interface RegisterAdminWithOtpPhoneRequest extends RegisterAdminWithOtpRequest {
    otpType: OtpType.PHONE
}

interface RegisterAdminWithGoogleAuthOtpRequest extends RegisterAdminWithOtpRequest {
    otpType: OtpType.GOOGLE_AUTH
}

interface RegisterAdminWithPhoneOtpResponse {
    phoneLastDigits: string
}

export interface RegisterAdminWithGoogleAuthOtpResponse {
    qrcode: string
    secret: string
}

type RegisterAdminWithOtpResponse<T> = T extends RegisterAdminWithOtpPhoneRequest
    ? RegisterAdminWithPhoneOtpResponse
    : T extends RegisterAdminWithGoogleAuthOtpRequest
    ? RegisterAdminWithGoogleAuthOtpResponse
    : never

export async function registerAdminWithOtp<T extends RegisterAdminWithOtpRequest>(payload: T) {
    const result = await axios.request<RegisterAdminWithOtpResponse<T>>({
        url: `${baseUrl}/sca/register/validate`,
        method: 'post',
        withCredentials: true,
        data: payload,
    })
    return result.data
}

interface ConfirmAdminOtpRequest {
    firstName: string
    lastName?: string
    jobTitle?: string
    email: string
    passcode: string
    phone: string
    otp: string
    language?: SupportedLanguage
    inviteToken?: string
    otpType?: OtpType
}

interface ConfirmAdminOtpResponse {
    accessToken: string
}

export async function confirmAdminWithOtp(
    payload: ConfirmAdminOtpRequest,
): Promise<ConfirmAdminOtpResponse> {
    const result = await axios.request<ConfirmAdminOtpResponse>({
        url: `${baseUrl}/sca/register`,
        method: 'post',
        withCredentials: true,
        data: payload,
    })
    return result.data
}

interface CheckEmployee {
    verifyToken: string
    userId: string
}

export async function checkEmployee(payload: CheckEmployee): Promise<void> {
    const result = await axios.request<void>({
        url: `${baseUrl}/sca/verify/check`,
        method: 'post',
        data: payload,
    })
    return result.data
}

export enum OtpType {
    PHONE = 'phone',
    GOOGLE_AUTH = 'googleAuth',
}

interface CreateEmployeeAndSendOtpRequest {
    verifyToken: string
    userId: string
    passcode: string
    phone?: string
    firstName: string
    lastName: string
    jobTitle?: string
    otpType?: OtpType
    language?: SupportedLanguage
}

export interface CreateEmployeeAndSendOtpResponse {
    phoneLastDigits?: string
    qrcode?: string
    secret?: string
}

export async function verifyWithOtp(
    payload: CreateEmployeeAndSendOtpRequest,
): Promise<CreateEmployeeAndSendOtpResponse> {
    const result = await axios.request<CreateEmployeeAndSendOtpResponse>({
        url: `${baseUrl}/sca/verify/validate`,
        method: 'post',
        withCredentials: true,
        data: payload,
    })
    return result.data
}

interface ConfirmEmployeeOtpRequest {
    verifyToken: string
    userId: string
    passcode: string
    otp: string
    firstName: string
    lastName: string
    jobTitle?: string
    otpType: OtpType
}

interface ConfirmEmployeeOtpResponse {
    accessToken: string
}

export async function verifyUser(
    payload: ConfirmEmployeeOtpRequest,
): Promise<ConfirmEmployeeOtpResponse> {
    const result = await axios.request<ConfirmEmployeeOtpResponse>({
        url: `${baseUrl}/sca/verify`,
        method: 'post',
        withCredentials: true,
        data: payload,
    })
    return result.data
}

interface ValidatePartnerWithOtpRequest {
    verifyToken: string
    userId: string
    passcode: string
    phone?: string
    otpType?: 'phone' | 'googleAuth'
    language?: SupportedLanguage
}

export interface ValidatePartnerWithOtpResponse {
    phoneLastDigits?: string
    qrcode?: string
    secret?: string
}

export async function validatePartnerWithOtp(
    payload: ValidatePartnerWithOtpRequest,
): Promise<ValidatePartnerWithOtpResponse> {
    const result = await axios.request<ValidatePartnerWithOtpResponse>({
        url: `${baseUrl}/sca/verify/validate`,
        method: 'post',
        withCredentials: true,
        data: payload,
    })
    return result.data
}

interface VerifyPartnerOtpRequest {
    verifyToken: string
    userId: string
    passcode: string
    otp: string
    otpType?: 'phone' | 'googleAuth'
    trust?: boolean
}

interface VerifyPartnerOtpResponse {
    accessToken: string
}

export async function verifyPartnerWithOtp(
    payload: VerifyPartnerOtpRequest,
): Promise<VerifyPartnerOtpResponse> {
    const result = await axios.request<VerifyPartnerOtpResponse>({
        url: `${baseUrl}/sca/verify`,
        method: 'post',
        withCredentials: true,
        data: payload,
    })
    return result.data
}
